import View from '@/components/view/View';
import * as R from '@/constants/systemRoutes';
import { getOnStorage, setOnStorage } from '@/shared';
import { RiSignalWifiErrorLine } from "react-icons/ri";
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Header, CheckoutProgressBar } from '@/components';
import { useCart, useClient, useGlobals, useNetworkStatusContext, useRemoteConfig } from '@/hooks';
import { OfflineMessage, OfflineMessageContainerStyle, OfflineOffset } from './container.style';

interface Props {
  children: any;
}

// milisegundos * segundos * minutos * horas * dias = 30 dias pois é o periodo de consulta das mensagens
const MAX_TIME_TO_KEEP_RECADOS = 1000 * 60 * 60 * 24 * 30;
const keepAliveRoutes = [R.ECOMMERCE.link, R.SEARCH.link, R.CATEGORY.link];
const SHOW_AMBIENT_FLAG = window.agp_config?.SHOW_AMBIENT_FLAG;
const SHOW_FLAG = !!SHOW_AMBIENT_FLAG && SHOW_AMBIENT_FLAG === 'true';

const SCROLL_DELTA_THRESHOLD = 12;
const SCROLL_DIRECTION_LOCK_MS = 320;
const BOTTOM_ZONE_PX = 72;
const TOP_ZONE_PX = 10;
const MIN_SCROLL_RANGE_TO_HIDE_CHROME = 80;

const Container = ({ children }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStarted = useRef(false);
  const { isSearching } = useCart();
  const scrollPositionRef = useRef(0);
  const scrollLockUntilRef = useRef(0);
  const lastAppliedDirectionRef = useRef<'up' | 'down' | null>(null);
  const { selectedClient } = useClient();
  const isStartedCheckIfIsEnabled = useRef(false);
  const { isOffline } = useNetworkStatusContext();
  const { scrollPosition, isScrolled, setScrollDirection } = useGlobals();

  useEffect(() => {
    const getScrollItems = () => document.querySelectorAll('.scroll-down, .scroll-up');

    const getMaxScroll = () => Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

    const commitScrollDirection = (direction: 'up' | 'down') => {
      if (lastAppliedDirectionRef.current === direction) return;

      lastAppliedDirectionRef.current = direction;
      scrollLockUntilRef.current = Date.now() + SCROLL_DIRECTION_LOCK_MS;
      scrollPositionRef.current = window.scrollY;
      setScrollDirection(direction);

      getScrollItems().forEach((item) => {
        item.classList.toggle('is-scrolled', direction === 'down');
      });
    };

    const handleScroll = () => {
      if (Date.now() < scrollLockUntilRef.current) return;

      const currentY = window.scrollY;
      const maxScroll = getMaxScroll();
      const delta = currentY - scrollPositionRef.current;

      if (maxScroll <= MIN_SCROLL_RANGE_TO_HIDE_CHROME) {
        commitScrollDirection('up');
        scrollPositionRef.current = currentY;
        return;
      }

      if (currentY <= TOP_ZONE_PX) {
        commitScrollDirection('up');
        scrollPositionRef.current = currentY;
        return;
      }

      if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) return;

      const nearBottom = currentY >= maxScroll - BOTTOM_ZONE_PX;
      if (nearBottom) {
        if (delta > 0) {
          commitScrollDirection('down');
        }
        scrollPositionRef.current = currentY;
        return;
      }

      if (delta > 0) {
        commitScrollDirection('down');
      } else {
        commitScrollDirection('up');
      }

      scrollPositionRef.current = currentY;
    };

    const initTimeout = setTimeout(() => {
      scrollPositionRef.current = window.scrollY;
      window.addEventListener('scroll', handleScroll, { passive: true });
    }, 600);

    return () => {
      clearTimeout(initTimeout);
      window.removeEventListener('scroll', handleScroll);
      scrollPositionRef.current = 0;
      scrollLockUntilRef.current = 0;
      lastAppliedDirectionRef.current = null;
    };
  }, [setScrollDirection]);

  useEffect(() => {
    if (!location.pathname.includes('login')) {
      setOnStorage('last_route', location.pathname);
    }

    const saved = scrollPosition;

    const isDashboardRoute = location.pathname.includes(R.DASHBOARD.link);
    const isKeepAliveRoute = keepAliveRoutes.some((route) => location.pathname.startsWith(route));

    // DASHBOARD ainda respeita o scroll salvo global
    if (isDashboardRoute) {
      if (saved > 0) {
        setTimeout(() => {
          window.scrollTo({
            top: saved,
            behavior: 'smooth',
          });
        }, 200);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else if (!isKeepAliveRoute) {
      // para rotas que NÃO usam KeepAlive, sempre volta pro topo
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 200);
    }

    return () => { };
  }, [location.pathname]);

  const checkIfIsEnabled = useCallback(
    (count: number) => {
      if (isStartedCheckIfIsEnabled.current) return;

      const enabledRoutes = [
        R.LOGIN.link,
        R.COMPANY.link,
        R.DASHBOARD.link,
        R.MY_CLIENTS.link,
        R.SALES_CYCLE.link,
        R.USER_DATA.link,
        R.ORDER_STATUS_PAGE.link,
        R.PRODUCTIVITY_EXTRACT.link,
        R.INDICATORS.link,
        R.MESSAGES.link,
        R.MESSAGE.link,
        R.SALES_REPORT.link,
        R.NEW_CLIENT.link,
        R.PERFORMANCE_INDICATORS.link,
      ];

      const isEnabledRoutes = enabledRoutes.some((route) => {
        if (location?.pathname === route) return true;

        if (route.includes(':')) {
          const routeBase = route.replace(/\/:[^/]+/g, '');
          return location?.pathname?.startsWith(routeBase + '/');
        }

        return false;
      });

      if (count === 5) return navigate(R.DASHBOARD.link);
      if (selectedClient?.COD_CLIENTE || isEnabledRoutes) return (isStartedCheckIfIsEnabled.current = true);

      setTimeout(() => {
        checkIfIsEnabled(count + 1);
      }, 1000);
    },
    [selectedClient?.COD_CLIENTE, location?.pathname, navigate]
  );

  useEffect(() => {
    checkIfIsEnabled(0);
  }, [selectedClient?.COD_CLIENTE, checkIfIsEnabled]);

  const validateRecados = useCallback(() => {
    const recados = getOnStorage('recados');
    const now = new Date();
    if (!!recados) {
      Object.keys(recados).forEach((key) => {
        const recadoDate = new Date(recados[Number(key)]);
        const diffInMilliseconds = now.getTime() - recadoDate.getTime();
        if (diffInMilliseconds > MAX_TIME_TO_KEEP_RECADOS) {
          delete recados[Number(key)];
        }
      });
      setOnStorage('recados', recados);
    }
  }, []);

  useEffect(() => {
    if (isStarted.current) return;
    isStarted.current = true;

    validateRecados();
  }, []);

  return (
    <View>
      {isOffline && (
        <>
          <OfflineMessageContainerStyle $isScrolled={isScrolled}>
            <OfflineMessage>
              <RiSignalWifiErrorLine size={24} />

              Você esta Offline!
            </OfflineMessage>
          </OfflineMessageContainerStyle>
          <OfflineOffset />
        </>
      )}

      <Header
        routeLabelSize={location.pathname === R.ORDER_STATUS_PAGE.link ? 16 : 0}
        rightIcon={null}
        stickChild={location.pathname === R.CART.link && !isSearching && <CheckoutProgressBar />}
      />

      {SHOW_FLAG && (
        <div style={{ position: 'fixed', marginLeft: 120, top: 0, left: '50%', transform: 'translateX(-50%)', width: 'fit-content', backgroundColor: '#00ff00', zIndex: 9999999999, padding: '5px 5px 2px 5px', borderBottomRightRadius: 4, borderBottomLeftRadius: 4 }}>
          <p style={{ fontSize: 9, fontWeight: 900, color: 'black' }}>PROD</p>
        </div>
      )}

      {children}
      <Menu />
    </View>
  );
};

export default Container;
